import Box from "@mui/material/Box";
import { Theme } from "@mui/material";
import { styled } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { NotAuthorized } from "_shared/_errors/_views/not-authorized";
import { useDialog } from "_shared/_providers/dialogs/dialog";
import { RootState, useAttorneyHubDispatch } from "_shared/_stores/redux-store";
import Loader from "_shared/_views/loading/loader";
import Guid from "_shared/guids/guid";
import ActiveTeamDisplay from "_shared/teams/active-team-display";
import CompanyColumnCell from "_shared/teams/cells/company-column-cell";
import LeaderColumnCell from "_shared/teams/cells/leader-column-cell";
import MembersColumnCell from "_shared/teams/cells/members-column-cell";
import {
  LowerTopToolbarContainer,
  TableTitle,
  TableTopToolbarContainer,
  TeamsTabs,

  ToolbarButtonsContainer,
} from "_shared/teams/teams-page";
import { PageTab } from "_shared/teams/teams-tabs";
import { uniqBy } from "lodash";
import { useGetIndividualsByUserIdsQuery } from "marketplace/individuals/_api/individual-api";
import {
  type MRT_ColumnFiltersState as ColumnFiltersState,
  createMRTColumnHelper,
  type MRT_TableOptions,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_GlobalFilterTextField as GlobalFilterTextField,
} from "material-react-table";
import { getSessionPermissions, populateSessionPermissions } from "permissions/_stores/permissions-redux-slice";
import * as React from "react";
import { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { useLoaderData } from "react-router";
import { useSession } from "users/__session/session-context";
import {
  getErrorLoadingTeamsByQuery,
  getIsLoadingTeamsByQuery,
  getTeamsByQuery,
  getTeamsQueryByInterface,
  updateTeamsInterfaceQuery,
} from "work/work-agreements/teams/vendor-team/_store/vendor-teams-redux-slice";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import {
  MobileToolbarButtonsContainer,
  searchFieldSpacing,
  ToolbarButtons,
  searchTextFieldProps,
  topToolbarFitsThePage,
} from "_shared/_views/tables/table-toolbar";
import {
  useGetActiveClientTeamsQuery,
  useGetActiveVendorTeamsQuery,
} from "work/work-agreements/teams/_api/teams-api-service";
import ClientTeam from "work/work-agreements/teams/client-team/client-team";

const LoaderContainer = styled(Box)(() => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
}));

const TeamDetailsContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(4),
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  padding: theme.spacing(1, 0, 2),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));

const TeamDetailsHeading = styled("h3")(({ theme }) => ({
  fontSize: "1.1rem",
  margin: theme.spacing(0, 0, 1),
}));

const columnHelper = createMRTColumnHelper<ActiveTeamDisplay>();
const columns = [
  columnHelper.accessor((team) => <CompanyColumnCell activeTeam={team} />, {
    header: "Company",
    id: "company",
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor((team) => <LeaderColumnCell activeTeam={team} />, {
    header: "Leader",
    id: "leader",
    enableSorting: false,
    enableColumnFilter: false,
  }),
  columnHelper.accessor((team) => <MembersColumnCell activeTeam={team} />, {
    header: "Members",
    id: "members",
    enableSorting: false,
    enableColumnFilter: false,
  }),
];

export default function ActiveTeams() {
  // State
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [globalFilter, setGlobalFilter] = React.useState<string | undefined>();

  // Route
  const routeParams = useLoaderData();
  const activePageTab = routeParams.pageTab ? (routeParams.pageTab as PageTab) : PageTab.Templates;
  const searchSubject = activePageTab === PageTab.TeamsWorkingForMe ? "teams working for me" : "teams I'm on";

  // Hooks
  const session = useSession();
  const { openDialog } = useDialog();
  const sessionPermissions = useSelector(getSessionPermissions);
  const sessionPermissionsLoading = useSelector((state: RootState) => state.permissions.session.loading);
  const sessionPermissionsError = useSelector((state: RootState) => state.permissions.session.error);
  const isMediumDisplaySize = useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const dispatch = useAttorneyHubDispatch();

  // Queries
  const interfaceName = "ActiveTeamsPage";
  const workingForMe = activePageTab === PageTab.TeamsWorkingForMe;
  const showingClientTeams = !session.context?.viewingAsVendor && !workingForMe;
  const vendorTeamsQuery = useSelector(getTeamsQueryByInterface(interfaceName));
  const vendorTeamsResponse = useSelector(getTeamsByQuery(interfaceName, workingForMe));
  const isLoadingVendorTeams = useSelector(getIsLoadingTeamsByQuery(interfaceName, workingForMe));
  const errorLoadingVendorTeams = useSelector(getErrorLoadingTeamsByQuery(interfaceName, workingForMe));
  const activeVendorTeamsQuery = useGetActiveVendorTeamsQuery(
    { interfaceName, workingForMe },
    { skip: showingClientTeams },
  );
  const activeClientTeamsQuery = useGetActiveClientTeamsQuery(
    { interfaceName },
    { skip: !showingClientTeams },
  );
  const clientTeamsResponse = useMemo(
    () =>
      activeClientTeamsQuery.data
        ?.map((team) => ClientTeam.fromJSON(team))
        .filter((team): team is ClientTeam => Boolean(team)),
    [activeClientTeamsQuery.data],
  );
  const activeTeamsResponse: ActiveTeamDisplay[] | undefined = showingClientTeams
    ? clientTeamsResponse
    : vendorTeamsResponse;
  const isLoadingTeams = showingClientTeams
    ? activeClientTeamsQuery.isLoading || activeClientTeamsQuery.isFetching
    : isLoadingVendorTeams || activeVendorTeamsQuery.isFetching;
  const errorLoadingTeams = showingClientTeams
    ? activeClientTeamsQuery.isError
    : Boolean(errorLoadingVendorTeams || activeVendorTeamsQuery.isError);

  const activeTeamUserIds = getActiveTeamUserIds(activeTeamsResponse || []);

  useEffect(() => {
    if (sessionPermissions || sessionPermissionsError || sessionPermissionsLoading) return;
    dispatch(populateSessionPermissions({ session }));
  }, [dispatch, session, sessionPermissions, sessionPermissionsError, sessionPermissionsLoading]);

  useEffect(() => {
    if (showingClientTeams) return;
    if (vendorTeamsQuery?.workingForMe === workingForMe) return;
    dispatch(updateTeamsInterfaceQuery({ interfaceName, workingForMe }));
  }, [dispatch, showingClientTeams, vendorTeamsQuery, workingForMe]);

  useGetIndividualsByUserIdsQuery(
    { userIds: new Set(activeTeamUserIds.map((id) => id.value)) },
    { skip: activeTeamUserIds.length === 0 },
  );

  const openTeamDetails = React.useCallback(
    (team: ActiveTeamDisplay, companyName?: string) => {
      openDialog({
        title: companyName ? `Team Details for ${companyName}` : "Team Details",
        MuiProps: { maxWidth: "md", fullWidth: true },
        component: <ActiveTeamDetails team={team} />,
      });
    },
    [openDialog],
  );

  const tableData = useMemo(() => activeTeamsResponse ?? [], [activeTeamsResponse]);

  const tableOptions: MRT_TableOptions<ActiveTeamDisplay> = {
    columns,
    data: tableData,
    enableRowSelection: false,
    enableTableHead: true,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => {
        openTeamDetails(row.original);
      },
      sx: { cursor: "pointer" },
    }),
    renderToolbarInternalActions: ({ table }) => (
      // Narrow screens get this same set from the custom actions below, where it can sit under the
      // tabs instead of beside them. Without the guard both rows draw and the page shows two of
      // every control, with the search field crushed to nothing between them.
      !isMediumDisplaySize && (
        <ToolbarButtonsContainer>
          <ToolbarButtons>
            <GlobalFilterTextField table={table} sx={searchFieldSpacing} />
            <ToggleFiltersButton table={table} />
            <ShowHideColumnsButton table={table} />
            <ToggleFullScreenButton table={table} />
          </ToolbarButtons>
        </ToolbarButtonsContainer>
      )
    ),
    renderTopToolbarCustomActions: ({ table }) => {
      return (
        <TableTopToolbarContainer>
          <TableTitle variant="h4">Teams</TableTitle>
          <LowerTopToolbarContainer>
            <TeamsTabs activeTab={activePageTab} />
          </LowerTopToolbarContainer>
          {isMediumDisplaySize && (
            <MobileToolbarButtonsContainer>
              <GlobalFilterTextField table={table} />
              <ToolbarButtons>
                <ToggleFiltersButton table={table} />
                <ShowHideColumnsButton table={table} />
                <ToggleFullScreenButton table={table} />
              </ToolbarButtons>
            </MobileToolbarButtonsContainer>
          )}
        </TableTopToolbarContainer>
      );
    },
    getRowId: (row, index) =>
      `${row.leader?.userId?.value || "no-leader"}-${row.members?.map((m) => m.userId.value).join("-")}-${index}`,
    enableRowActions: false,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: [] },
      showGlobalFilter: true,
    },
    positionGlobalFilter: "none",
    muiSearchTextFieldProps: ({ table }) => searchTextFieldProps({ searches: searchSubject, table }),
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: errorLoadingTeams,
      isLoading: isLoadingTeams,
      onRetry: () => void (showingClientTeams ? activeClientTeamsQuery.refetch() : activeVendorTeamsQuery.refetch()),
      subject: searchSubject,
    }),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    manualPagination: true,
    manualSorting: true,
    muiTableContainerProps: {
      sx: {
        flex: 1,
        minWidth: 0,
      },
    },
    muiTablePaperProps: {
      sx: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        minWidth: 0,
        overflow: "hidden",
      },
    },
    muiTopToolbarProps: { sx: topToolbarFitsThePage },
    muiToolbarAlertBannerProps: errorLoadingTeams
      ? {
          color: "error",
          children: "Error loading data",
        }
      : undefined,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    rowCount: activeTeamsResponse?.length ?? 0,
    state: {
      columnFilters,
      globalFilter,
      isLoading: !activeTeamsResponse?.length && isLoadingTeams,
      pagination,
      showAlertBanner: errorLoadingTeams,
      showProgressBars: isLoadingTeams,
      sorting,
      showSkeletons: !activeTeamsResponse?.length && isLoadingTeams,
    },
  };

  if (!sessionPermissions)
    return (
      <LoaderContainer>
        <Loader fontSize="20px" />
      </LoaderContainer>
    );
  if (!sessionPermissions.includesAccessToTeams) return <NotAuthorized />;

  return <MRTNoMemo<ActiveTeamDisplay> options={tableOptions} />;
}

function ActiveTeamDetails({ team }: Readonly<{ team: ActiveTeamDisplay }>) {
  return (
    <TeamDetailsContainer>
      <Box>
        <TeamDetailsHeading>Team Leader</TeamDetailsHeading>
        <LeaderColumnCell activeTeam={team} />
      </Box>
      <Box>
        <TeamDetailsHeading>Team Members</TeamDetailsHeading>
        <MembersColumnCell activeTeam={team} showAllMembers />
      </Box>
    </TeamDetailsContainer>
  );
}

function getActiveTeamUserIds(teams: ActiveTeamDisplay[]): Guid[] {
  if (teams.length === 0) return [];
  return uniqBy(
    teams.flatMap((team) => [
      ...(team.leader?.userId ? [team.leader.userId] : []),
      ...(Array.isArray(team.members) ? team.members.map((m) => m.userId) : []),
    ]),
    (id) => id.value,
  );
}
